import * as THREE from 'three';

// Highway tier configuration
export interface HighwayTier {
  id: number;
  height: number;
  name: string;
  color: number;
}

// Predefined highway tiers
export const HIGHWAY_TIERS: HighwayTier[] = [
  { id: 0, height: 0, name: 'Ground', color: 0x00ffff },
  { id: 1, height: 8, name: 'Tier 1', color: 0xff00ff },
  { id: 2, height: 18, name: 'Tier 2', color: 0x8000ff },
  { id: 3, height: 30, name: 'Tier 3', color: 0xff0080 },
  { id: 4, height: 45, name: 'Tier 4', color: 0x00ff80 },
];

// Node in the highway network
export interface HighwayNode {
  id: string;
  position: THREE.Vector3;
  tier: number;
  type: 'endpoint' | 'junction' | 'waypoint';
  connections: string[];
}

// Highway segment configuration
export interface HighwaySegmentConfig {
  id: string;
  startPos: THREE.Vector3;
  endPos: THREE.Vector3;
  tier: number;
  width: number;
  type: 'straight' | 'ramp';
  // For ramps connecting tiers
  startHeight?: number;
  endHeight?: number;
  // Visual options
  hasGuardrails: boolean;
  hasNeonStripes: boolean;
  // Access
  requiresJump: boolean;
}

// Ramp connection between tiers
export interface TierConnection {
  id: string;
  type: 'ramp' | 'jump';
  fromTier: number;
  toTier: number;
  entry: THREE.Vector3;
  exit: THREE.Vector3;
}

// Complete highway network
export interface HighwayNetwork {
  tiers: HighwayTier[];
  nodes: Map<string, HighwayNode>;
  segments: HighwaySegmentConfig[];
  connections: TierConnection[];
}

// Result of height query
export interface HighwayHeightResult {
  height: number;
  onHighway: boolean;
  currentTier: number;
  segmentId: string | null;
}

// Get tier by height
export function getTierAtHeight(height: number): HighwayTier | null {
  // Find closest tier within tolerance
  const tolerance = 3;
  for (const tier of HIGHWAY_TIERS) {
    if (Math.abs(height - tier.height) < tolerance) {
      return tier;
    }
  }
  return null;
}

// Get tier color
export function getTierColor(tierId: number): number {
  const tier = HIGHWAY_TIERS.find(t => t.id === tierId);
  return tier ? tier.color : 0x00ffff;
}
